import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/** Full capability set for clinic_admin — must not be downgraded by staff record merges. */
export const CLINIC_ADMIN_PERMISSIONS = {
  sendReminders: true,
  createConsents: true,
  finaliseConsents: true,
  manageTemplates: true,
  viewStats: true,
  manageStaff: true,
  accessSettings: true,
  overrideConsents: true,
};

/**
 * Clinic admins always get level-4 permissions regardless of staff collection rows.
 */
export const applyClinicAdminPrivileges = (userData) => {
  if (!userData || userData.role !== 'clinic_admin') return userData;
  return {
    ...userData,
    role: 'clinic_admin',
    permissionLevel: 4,
    permissions: { ...CLINIC_ADMIN_PERMISSIONS },
  };
};

/**
 * Merge authoritative fields from users/{uid} (role, clinicId) — never leave clinic_admin as clinic_staff.
 */
export const enrichUserFromFirestore = async (baseData) => {
  if (!baseData?.uid) return applyClinicAdminPrivileges(baseData);
  try {
    const userDoc = await getDoc(doc(db, 'users', baseData.uid));
    if (!userDoc.exists()) return applyClinicAdminPrivileges(baseData);
    const d = userDoc.data();
    const merged = {
      ...baseData,
      role: d.role || baseData.role,
      clinicId: baseData.clinicId || d.clinicId || null,
      clinicName: baseData.clinicName || d.clinicName || null,
      displayName: baseData.displayName || d.displayName || null,
      isActive: d.isActive !== undefined ? d.isActive : baseData.isActive,
      onboardingCompleted:
        d.onboardingCompleted !== undefined
          ? d.onboardingCompleted
          : baseData.onboardingCompleted,
    };
    return applyClinicAdminPrivileges(merged);
  } catch (_) {
    return applyClinicAdminPrivileges(baseData);
  }
};

/** Alias used by AuthContext */
export const enrichFromUsersDocument = enrichUserFromFirestore;

/** Registration / UI defaults that are not a real practice name. */
const PLACEHOLDER_CLINIC_NAMES = new Set([
  'unknown clinic',
  'my practice',
  'clinic',
  'your dental clinic',
  'dental clinic',
  'your dental practice',
  'dental practice',
  'practice (removed)',
]);

export const isPlaceholderClinicName = (name) => {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return true;
  return PLACEHOLDER_CLINIC_NAMES.has(n);
};

/** Real practice name for patient-facing copy; empty if missing or placeholder. */
export const resolveClinicNameForPatientEmail = (...candidates) => {
  for (const candidate of candidates) {
    const n = String(candidate || '').trim();
    if (n && !isPlaceholderClinicName(n)) return n;
  }
  return '';
};

/** Resolve clinic display name — clinics/{id}.name is authoritative over denormalised user/staff fields. */
export const resolveClinicNameFromFirestore = async (clinicId, fallbackName = null) => {
  if (!clinicId) {
    return isPlaceholderClinicName(fallbackName) ? null : fallbackName || null;
  }
  try {
    const clinicDoc = await getDoc(doc(db, 'clinics', clinicId));
    if (clinicDoc.exists()) {
      const name = clinicDoc.data().name;
      if (name && !isPlaceholderClinicName(name)) return name;
    }
  } catch (_) {}
  return isPlaceholderClinicName(fallbackName) ? null : fallbackName || null;
};

/**
 * Apply staff login-hour fields only — do not downgrade clinic_admin permissions.
 */
export const mergeStaffLoginHours = (userData, staffPermissions) => {
  if (!userData || !staffPermissions) return userData;
  if (userData.role === 'clinic_admin') {
    return applyClinicAdminPrivileges({
      ...userData,
      loginHoursRestricted: staffPermissions.loginHoursRestricted,
      loginHours: staffPermissions.loginHours,
    });
  }
  return {
    ...userData,
    permissions: { ...(userData.permissions || {}), ...(staffPermissions.permissions || {}) },
    permissionLevel: staffPermissions.permissionLevel ?? userData.permissionLevel,
    loginHoursRestricted: staffPermissions.loginHoursRestricted,
    loginHours: staffPermissions.loginHours,
  };
};

/**
 * Merge staff row — clinic_admin keeps full access; staff get staff permissions.
 */
export const mergeStaffRecordFields = (userData, staffPermissions) => {
  if (!userData || !staffPermissions) return userData;
  const withClinic = {
    ...userData,
    clinicId: userData.clinicId || staffPermissions.clinicId,
    clinicName: userData.clinicName || staffPermissions.clinicName,
  };
  return mergeStaffLoginHours(withClinic, staffPermissions);
};

/**
 * Single source of truth: resolve complete userData with clinicId for clinic users.
 * Tries: users/{uid} first, then staff by email -> users by email.
 */
const finalizeClinicUserData = async (data) => {
  if (data?.clinicId) {
    data.clinicName = await resolveClinicNameFromFirestore(data.clinicId, data.clinicName);
  }
  return applyClinicAdminPrivileges(data);
};

export const resolveClinicUserData = async (baseData) => {
  if (!baseData || baseData.role === 'super_admin') return baseData;

  let data = await enrichUserFromFirestore(baseData);
  if (data.clinicId) {
    return finalizeClinicUserData(data);
  }

  const email = data.email;
  const uid = data.uid;

  // 1. Staff by email (primary - staff always has clinicId)
  if (email) {
    const sp = await getStaffPermissions(email);
    if (sp?.clinicId) {
      return finalizeClinicUserData(
        mergeStaffRecordFields(
          {
            ...data,
            clinicId: sp.clinicId,
            clinicName: sp.clinicName || data.clinicName,
          },
          sp
        )
      );
    }
  }

  // 2. getClinicIdForEmail (staff + users by email)
  if (email) {
    const clinicId = await getClinicIdForEmail(email);
    if (clinicId) {
      const sp = await getStaffPermissions(email);
      const withClinic = {
        ...data,
        clinicId,
        clinicName: sp?.clinicName || data.clinicName,
      };
      return finalizeClinicUserData(
        sp ? mergeStaffRecordFields(withClinic, sp) : withClinic
      );
    }
  }

  // 3. Users doc by uid (fallback if enrich did not run)
  if (uid) {
    data = await enrichUserFromFirestore(data);
    if (data.clinicId) {
      return finalizeClinicUserData(data);
    }
  }

  return finalizeClinicUserData(data);
};

/**
 * Show the logged-in person's own email/name in the UI (sidebar, My Account).
 * Prefer Firebase Auth email, then staff record by userId, then staff by email.
 */
export const resolveSessionIdentity = async (baseData, authUser = null) => {
  if (!baseData || baseData.role === 'super_admin') return baseData;

  const data = { ...baseData };
  const uid = data.uid || authUser?.uid;

  if (authUser?.email) {
    data.email = authUser.email;
  }

  let staffSnapshot = null;
  try {
    if (uid) {
      const byUid = query(collection(db, 'staff'), where('userId', '==', uid));
      const snap = await getDocs(byUid);
      if (!snap.empty) staffSnapshot = snap;
    }
    if (!staffSnapshot && data.email) {
      const byEmail = query(collection(db, 'staff'), where('email', '==', data.email));
      const snap = await getDocs(byEmail);
      if (!snap.empty) staffSnapshot = snap;
    }
  } catch (err) {
    console.error('resolveSessionIdentity: staff lookup failed', err);
  }

  if (staffSnapshot && !staffSnapshot.empty) {
    const staff = staffSnapshot.docs[0].data();
    if (staff.email) data.email = staff.email;
    const staffName =
      (staff.displayName && String(staff.displayName).trim()) ||
      [staff.firstName, staff.lastName].filter(Boolean).join(' ').trim();
    if (staffName) data.displayName = staffName;
  } else if (authUser?.displayName && !data.displayName) {
    data.displayName = authUser.displayName;
  }

  return data;
};

/**
 * Check if a user is allowed to login based on their login hour restrictions
 * @param {string} email - User's email address
 * @returns {Object} - Result object with allowed status and message
 */
export const checkLoginRestrictions = async (email) => {
  try {
    console.log('Checking login restrictions for email:', email);
    
    // Validate email parameter
    if (!email || typeof email !== 'string') {
      console.log('Invalid email parameter, allowing login');
      return { allowed: true, message: null };
    }
    
    // Find the staff record for this email
    const staffQuery = query(collection(db, 'staff'), where('email', '==', email));
    const staffSnapshot = await getDocs(staffQuery);
    
    if (staffSnapshot.empty) {
      console.log('No staff record found for email:', email, '- allowing login');
      // No staff record found, allow login (might be a different type of user)
      return { allowed: true, message: null };
    }
    
    const staffData = staffSnapshot.docs[0].data();
    console.log('Found staff data:', {
      loginHoursRestricted: staffData.loginHoursRestricted,
      loginHours: staffData.loginHours
    });
    
    // Check if login hours are restricted
    if (!staffData.loginHoursRestricted) {
      console.log('Login hours not restricted - allowing login');
      return { allowed: true, message: null };
    }
    
    // Get current time and day
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase(); // 'monday', 'tuesday', etc.
    const currentTime = now.toTimeString().slice(0, 5); // 'HH:MM' format
    
    console.log('Current time check:', {
      currentDay,
      currentTime,
      daySchedule: staffData.loginHours?.[currentDay]
    });
    
    const daySchedule = staffData.loginHours?.[currentDay];
    
    if (!daySchedule || !daySchedule.enabled) {
      const message = `Login not allowed on ${currentDay.charAt(0).toUpperCase() + currentDay.slice(1)}. Please contact your administrator.`;
      console.log('Login not allowed:', message);
      return { 
        allowed: false, 
        message 
      };
    }
    
    // Check if current time is within allowed hours
    const startTime = daySchedule.start;
    const endTime = daySchedule.end;
    
    console.log('Time comparison:', {
      startTime,
      endTime,
      currentTime,
      isBeforeStart: currentTime < startTime,
      isAfterEnd: currentTime > endTime
    });
    
    if (currentTime < startTime || currentTime > endTime) {
      const message = `Login hours for ${currentDay.charAt(0).toUpperCase() + currentDay.slice(1)} are ${startTime} - ${endTime}. Current time: ${currentTime}`;
      console.log('Login outside hours:', message);
      return { 
        allowed: false, 
        message 
      };
    }
    
    console.log('Login allowed within hours');
    return { allowed: true, message: null };
    
  } catch (error) {
    console.error('Error checking login restrictions:', error);
    // If there's an error checking restrictions, allow login to prevent blocking legitimate users
    return { allowed: true, message: null };
  }
};

/**
 * Get staff permissions for a given email
 * @param {string} email - User's email address
 * @returns {Object} - Staff permissions object
 */
export const getStaffPermissions = async (email) => {
  try {
    // Validate email parameter
    if (!email || typeof email !== 'string') {
      console.log('Invalid email parameter for staff permissions');
      return null;
    }
    
    const staffQuery = query(collection(db, 'staff'), where('email', '==', email));
    const staffSnapshot = await getDocs(staffQuery);
    
    if (staffSnapshot.empty) {
      return null;
    }
    
    const staffData = staffSnapshot.docs[0].data();
    return {
      permissions: staffData.permissions || {},
      permissionLevel: staffData.permissionLevel || 1,
      loginHoursRestricted: staffData.loginHoursRestricted || false,
      loginHours: staffData.loginHours || {},
      clinicId: staffData.clinicId || null,
      clinicName: staffData.clinicName || null
    };
  } catch (error) {
    console.error('Error getting staff permissions:', error);
    return null;
  }
};

/**
 * Get clinicId for a user by email - tries staff collection first, then users collection
 */
export const getClinicIdForEmail = async (email) => {
  try {
    if (!email || typeof email !== 'string') return null;
    // Try staff collection first
    const staffQuery = query(collection(db, 'staff'), where('email', '==', email));
    const staffSnapshot = await getDocs(staffQuery);
    if (!staffSnapshot.empty) {
      const clinicId = staffSnapshot.docs[0].data().clinicId || null;
      if (clinicId) return clinicId;
    }
    // Fallback: query users collection by email
    const usersQuery = query(collection(db, 'users'), where('email', '==', email));
    const usersSnapshot = await getDocs(usersQuery);
    if (!usersSnapshot.empty) {
      const clinicId = usersSnapshot.docs[0].data().clinicId || null;
      if (clinicId) return clinicId;
    }
    return null;
  } catch (error) {
    console.error('Error getting clinicId for email:', error);
    return null;
  }
};

/**
 * Check if a user has a specific permission
 * @param {Object} userData - User data object
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether user has the permission
 */
export const hasPermission = (userData, permission) => {
  if (!userData) return false;
  
  // Super admin has all permissions
  if (userData.role === 'super_admin') return true;
  
  // Clinic admin has all permissions
  if (userData.role === 'clinic_admin') return true;
  
  // Check custom permissions from staff record
  if (userData.permissions && userData.permissions[permission]) {
    return true;
  }
  
  // Check permission level based access
  if (userData.permissionLevel) {
    const permissionLevels = {
      1: ['sendReminders'], // Receptionist
      2: ['sendReminders', 'createConsents', 'finaliseConsents', 'viewStats'], // Dentist, Treatment Coordinator, Nurse
      3: ['sendReminders', 'createConsents', 'finaliseConsents', 'viewStats', 'manageTemplates'], // Clinical Lead, Practice Manager
      4: ['sendReminders', 'createConsents', 'finaliseConsents', 'manageTemplates', 'viewStats', 'manageStaff', 'accessSettings', 'overrideConsents'] // Clinic Admin
    };
    
    const levelPermissions = permissionLevels[userData.permissionLevel] || [];
    if (levelPermissions.includes(permission)) {
      return true;
    }
  }
  
  // Fallback to role-based permissions
  const rolePermissions = {
    clinic_admin: ['sendReminders', 'createConsents', 'finaliseConsents', 'manageTemplates', 'viewStats', 'manageStaff', 'accessSettings', 'overrideConsents'],
    clinic_staff: ['sendReminders', 'createConsents', 'finaliseConsents', 'viewStats']
  };
  
  return rolePermissions[userData.role]?.includes(permission) || false;
}; 